// The by-strategist table. Four rows, because four people are named in `roles.json`.
//
// Built from the configured list rather than from the rows that came back, which is a decision
// `activity.py` makes and this only renders: a strategist who sent nothing this period is a row
// of zeroes rather than a missing row. A missing row reads as "not on the team"; a zero reads
// as "did nothing this month", and only one of those is ever true.
//
// **`Met` and `Stage 2` are the two quota columns.** Both count accounts. Stage 2 is companies
// whose deal reached stage 2 or later this period within four weeks of a meeting the
// strategist sat in; the rule lives in activity.py and this only renders it.
//
// The meeting columns then say the same thing three ways on purpose. `Booked` and `Attended` are
// the same meetings at two moments and are never added; `Infl.` is the separate inference, kept
// beside its exact neighbours so a reader can see which number they are trusting.

import type { StrategistRow } from '../api/types';
import { formatCount, formatMoney } from '../lib/format';
import { isScoutRow } from './lanes';

// The Scout row is last and is not a person: the auto lane's sequences, whoever's mailbox sent
// them. Owned is its auto-lane accounts nobody has replied to yet; replies and meetings after a
// real reply are Scout-sourced and also counted under the strategist.
const SCOUT_ROW_TITLE = 'The auto lane’s sequences, whoever’s mailbox sent them. Owned is auto-lane '
  + 'accounts with no real reply yet. Replies and meetings after a real reply are Scout-sourced and '
  + 'also counted under the strategist.';

// A reply rate, or the raw fraction when there have not been enough sends to justify a
// percentage. Mirrors `_reply_rate` in activity.py, which returns null in exactly that case --
// so this renders the alternative rather than deciding the rule.
function ReplyRate({ row, minDenominator }: { row: StrategistRow; minDenominator: number }) {
  if (row.reply_rate != null) return <>{row.reply_rate}%</>;
  if (!row.sends) return <span className="zero">—</span>;
  return (
    <span
      className="thin"
      title={`Under ${minDenominator} sends, so the fraction is shown instead of a percentage.`}
    >
      {formatCount(row.replies)}/{formatCount(row.sends)}
    </span>
  );
}

export function StrategistTable({
  rows,
  minDenominator,
  selected,
  onSelect,
  today,
}: {
  rows: StrategistRow[];
  minDenominator: number;
  selected: string | null;
  onSelect: (email: string | null) => void;
  today: string;
}) {
  return (
    <div className="tablewrap" style={{ marginTop: 0 }}>
      <table className="strategists">
        <thead>
          <tr>
            <th>Strategist</th>
            <th className="num" title="Accounts carrying their SDR_OWNER_C. Not windowed.">
              Owned
            </th>
            <th className="num" title="Owned accounts nobody has ever touched. Not windowed.">
              Untouched
            </th>
            <th className="num" title="Accounts they sent something to in this period.">
              Worked
            </th>
            <th className="num" title="Distinct people written to in this period.">Contacts</th>
            <th className="num">Sends</th>
            <th className="num" title="Auto-replies are counted separately and excluded.">
              Replies
            </th>
            <th className="num">Reply rate</th>
            <th
              className="num quota"
              title="Accounts on their book where they attended a meeting this period. 75% of quota. One per company, however many sittings."
            >
              Met
            </th>
            <th
              className="num quota"
              title="Accounts on their book whose deal reached stage 2 or later in this period, within four weeks of a meeting they sat in. 25% of quota. Dated by the crossing, one per company."
            >
              Stage 2
            </th>
            <th className="num" title="Meetings scheduled this period, dated when they were booked. A standing call counts once per month it was booked in.">
              Booked
            </th>
            <th className="num" title="Sittings they were on, dated when the meeting ran. Cancellations and Gong-confirmed no-shows are excluded.">
              Attended
            </th>
            <th
              className="num"
              title="Meetings they own in Salesforce, counted as acts of booking in this period. Exact. The book counts sittings all time, so the two do not have to match."
            >
              Theirs
            </th>
            <th
              className="num"
              title="Meetings somebody else owns, booked after their first touch on the account."
            >
              Infl.
            </th>
            <th className="num" title="Deals opened after their first touch on the account.">
              Opps
            </th>
            <th className="num">Pipeline</th>
            <th>Last active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const isSelected = selected === row.sdr_owner_email;
            // Days since their last touch, so a stalled rep is visible without arithmetic.
            const quietFor = row.last_active
              ? Math.round((new Date(today + 'T00:00:00Z').getTime()
                - new Date(row.last_active + 'T00:00:00Z').getTime()) / 864e5)
              : null;
            return (
              <tr
                key={row.sdr_owner_email ?? row.sdr_owner}
                className={'acct' + (isSelected ? ' open' : '') + (isScoutRow(row) ? ' scoutrow' : '')}
                tabIndex={0}
                role="button"
                aria-pressed={isSelected}
                aria-label={`${row.sdr_owner} — scope the page to them`}
                onClick={() => onSelect(isSelected ? null : row.sdr_owner_email)}
                onKeyDown={event => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onSelect(isSelected ? null : row.sdr_owner_email);
                }}
              >
                <td className="acctname" title={isScoutRow(row) ? SCOUT_ROW_TITLE : undefined}>{row.sdr_owner}</td>
                <td className="num">{formatCount(row.accounts_owned)}</td>
                <td className={'num' + (row.accounts_untouched ? ' warncell' : ' zero')}>
                  {formatCount(row.accounts_untouched)}
                </td>
                <td className={'num' + (row.accounts_worked ? '' : ' zero')}>
                  {formatCount(row.accounts_worked)}
                </td>
                <td className={'num' + (row.contacts_reached ? '' : ' zero')}>
                  {formatCount(row.contacts_reached)}
                </td>
                <td className={'num' + (row.sends ? '' : ' zero')}>{formatCount(row.sends)}</td>
                <td className={'num' + (row.replies ? '' : ' zero')}>
                  {formatCount(row.replies)}
                </td>
                <td className="num"><ReplyRate row={row} minDenominator={minDenominator} /></td>
                <td className={'num quota' + (row.accounts_met_attended ? ' good' : ' zero')}>
                  {formatCount(row.accounts_met_attended)}
                </td>
                <td className={'num quota' + (row.accounts_to_stage_2 ? ' good' : ' zero')}>
                  {formatCount(row.accounts_to_stage_2)}
                </td>
                <td className={'num' + (row.meetings_booked ? '' : ' zero')}>
                  {formatCount(row.meetings_booked)}
                </td>
                <td className={'num' + (row.meetings_attended ? ' good' : ' zero')}>
                  {formatCount(row.meetings_attended)}
                </td>
                <td className={'num' + (row.meetings_owned ? '' : ' zero')}>
                  {formatCount(row.meetings_owned)}
                </td>
                <td className={'num' + (row.meetings_influenced ? '' : ' zero')}>
                  {formatCount(row.meetings_influenced)}
                </td>
                <td className={'num' + (row.opportunities ? ' good' : ' zero')}>
                  {formatCount(row.opportunities)}
                </td>
                <td className={'num' + (row.qualified_pipeline ? '' : ' zero')}>
                  {row.qualified_pipeline ? formatMoney(row.qualified_pipeline) : '$0'}
                </td>
                <td className="sm nowrap">
                  {row.last_active
                    ? <>{row.last_active}{quietFor != null && quietFor > 7
                        ? <div className="sm stalemark">{quietFor}d ago</div> : null}</>
                    : <span className="zero">nothing this period</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
